import { type RoomType } from "@/shared/server";
import { Box, Center, chakra, Flex, Spinner, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export const RoomPictures = ({ targetIdx, rooms }: { targetIdx: number; rooms: RoomType[] }) => {
    const [focusImgIndex, setFocusImgIndex] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const targetRoom = rooms.find((t) => t.room_idx === String(targetIdx));
    /* eslint-disable consistent-return */
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.deltaY !== 0) {
                e.preventDefault();
                el.scrollTo({
                    left: el.scrollLeft + e.deltaY,
                });
            }
        };

        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [targetRoom]);
    /* eslint-enable consistent-return */
    useEffect(() => {
        setFocusImgIndex(0);
    }, [targetIdx]);

    const prevSlide = () => {
        if (targetRoom) {
            setFocusImgIndex((prev) => {
                if (prev === 0) return targetRoom.imgList.length - 1;
                return prev - 1;
            });
        }
    };
    const nextSlide = () => {
        if (targetRoom) {
            setFocusImgIndex((prev) => {
                if (prev === targetRoom.imgList.length - 1) return 0;
                return prev + 1;
            });
        }
    };
    return targetRoom ? (
        <Flex w="100%" h="100%" flexDir="column">
            <Box position="relative" w="100%" h="652px">
                {targetRoom.imgList.map((img, index) => (
                    <MotionBox
                        key={img.img_URL}
                        position="absolute"
                        w="100%"
                        h="100%"
                        bg={img.img_URL ? `url(${img.img_URL})no-repeat center/cover` : "gray.100"}
                        initial={{ opacity: index === focusImgIndex ? 1 : 0 }}
                        animate={{
                            opacity: focusImgIndex === index ? 1 : 0,
                            transition: { duration: 0.7, ease: "easeIn" },
                        }}
                    />
                ))}
                <Center
                    w="56px"
                    h="56px"
                    position="absolute"
                    left="0"
                    top="50%"
                    transform="translateY(-50%)"
                    bg="blackAlpha.400"
                    onClick={() => prevSlide()}
                >
                    <Text className="material-icons-outlined" cursor="pointer" color="#fff">
                        arrow_back_ios
                    </Text>
                </Center>
                <Center
                    w="56px"
                    h="56px"
                    position="absolute"
                    right="0"
                    top="50%"
                    transform="translateY(-50%)"
                    color="#fff"
                    bg="blackAlpha.400"
                >
                    <Text className="material-icons-outlined" cursor="pointer" onClick={() => nextSlide()}>
                        arrow_forward_ios
                    </Text>
                </Center>
            </Box>
            <Box ref={scrollRef} w="100%" overflowX="auto">
                <Flex gap="12px" w="max-content" pt="24px">
                    {targetRoom.imgList.map((img, index) => (
                        <Box
                            flexShrink="0"
                            key={img.img_URL}
                            w="144px"
                            h="120px"
                            borderRadius="8px"
                            bg={`url(${img.img_URL})no-repeat center/cover`}
                            cursor="pointer"
                            onClick={() => setFocusImgIndex(index)}
                        />
                    ))}
                </Flex>
            </Box>
        </Flex>
    ) : (
        <Center h="100%">
            <Text>Loading...</Text>
            <Spinner />
        </Center>
    );
};

const MotionBox = chakra(motion.div);
